import React, { useMemo, useState } from 'react';
import { Button, Dropdown, MenuProps, Select, Tooltip } from 'antd';
import './AppDetailSelector.scss';
import { RootState } from 'store/reducer';
import { AppVersion } from 'data/terra/AppVersion';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Environment } from 'data/terra/Environment';
import { Platform } from 'data/terra/Platform';
import { PlatformCode } from 'data/terra/ModuleConfig/util';
import { App } from 'data/terra/App';
import { getActiveEnv, getEnvDisplayName, isEnvActiveEnv } from 'util/envUtil';
import { showError, showLoading, showSuccess } from 'store/popup/actions';
import { runAsync } from 'util/async';
import { downloadPandoraConfig } from 'util/pandoraConfig';
import { getEstimateWidth } from 'util/selectOptions';
import { selectApp } from 'v2/store/projectDetail/actions/SelectApp';
import { selectAppVersion } from 'v2/store/projectDetail/actions/SelectAppVersion';
import { selectEnv } from 'v2/store/projectDetail/actions/SelectEnv';
import ClientQrPopup from 'v2/components/client/ClientQrPopup';
import AddVersionPopup from 'v2/components/version/AddVersionPopup';
import BuildAppPopup from 'v2/components/buildapp/BuildAppPopup';
import { ElementIds } from '../configs';
import { hideLoading } from '../../../../store/loading/actions';
import {
  AndroidIcon,
  DownloadIcon,
  InstallmentIcon,
  IosIcon,
  MoreVerticalIcon,
  QrCodeIcon,
  SyncIcon,
} from 'v2/containers/Icons';
import { t } from 'helpers/i18n';
import {
  AppConfig,
  CreateAppConfigRequest,
  createAppConfigTemplate,
} from 'data/terra/AppConfig';
import Terra from 'services/apis/terra/Terra';
import { buildClientId } from 'util/clientIdBuilder';
import VersionNamingRulePopup from 'v2/components/version/VersionNamingRulePopup';
import { Result } from 'util/result';
import ResultModal, { ResultModalProps } from 'v2/components/modal/ResultModal';
import { BuildAppVersionResponse } from 'data/terra/api/terra/response/BuildAppVersionResponse';
import { asAxiosError } from 'util/resultExt';
import { ErrorResponse } from 'data/terra/api/terra/base/ErrorResponse';

enum MenuAction {
  DownloadConfig = 'download_config',
  ShowQrCode = 'show_qr_code',
}

const AppDetailSelector: React.FC = () => {
  const dispatch = useDispatch();
  const {
    environments,
    platforms,
    apps,
    appVersions,
    selectedEnv,
    selectedApp,
    selectedAppVersion,
    appConfigs,
    visibleSyncConfigButton,
  } = useSelector(stateSelector, shallowEqual);

  const [filteredAppVersions, setFilteredAppVersions] = useState<AppVersion[]>(
    appVersions
  );

  const activeEnv = useMemo(() => getActiveEnv(appConfigs, environments), [
    appConfigs,
    environments,
  ]);
  const isSelectedEnvActiveEnv = useMemo(
    () => (selectedEnv ? isEnvActiveEnv(selectedEnv, appConfigs) : true),
    [selectedEnv, appConfigs]
  );

  const changeEnvironmentConfig = async () => {
    if (selectedApp && selectedAppVersion && selectedEnv) {
      dispatch(showLoading());
      let configWantToActivate = appConfigs.find(
        (ac: AppConfig) => ac.env.id === selectedEnv.id
      );
      if (!configWantToActivate) {
        // create default app config for this env and active it
        const defaultConfig = createAppConfigTemplate(selectedEnv!, undefined);
        const result = await Terra.appConfig.createAppConfig(
          selectedApp.id,
          selectedAppVersion.id,
          defaultConfig as CreateAppConfigRequest
        );
        if (result.isSuccess()) {
          configWantToActivate = result.get()!.data;
        } else {
          dispatch(showError(result.exception().message));
          return;
        }
      }

      if (configWantToActivate) {
        const result = await Terra.appConfig.activateAppConfig(
          selectedApp.id,
          selectedAppVersion.id,
          configWantToActivate.id
        );
        if (result.isSuccess()) {
          await Terra.frontEnd.invalidatePandoraCache(
            buildClientId(selectedApp, selectedAppVersion)
          );
          dispatch(selectAppVersion(selectedAppVersion)); // to trigger refetch app configs
          dispatch(showSuccess());
        } else {
          dispatch(showError(result.exception().message));
        }
      } else {
        dispatch(showError(t('No configs to activate')));
      }
    } else {
      dispatch(
        showError(t('Need select app, app version and environment first'))
      );
    }
  };

  const [showQR, setShowQR] = useState<boolean>(false);

  const [showBuildApp, setShowBuildApp] = useState<boolean>(false);

  const [buildAppResult, setBuildAppResult] = useState<ResultModalProps>({
    visible: false,
    isSuccess: false,
  });

  const [showAddVersion, setShowAddVersion] = useState<boolean>(false);

  const [showVersionNamingRule, setShowVersionNamingRule] = useState<boolean>(
    false
  );

  let estimatedVersionWidth = getEstimateWidth(
    appVersions.map(v => v.code),
    7
  );
  let estimatedEnvWidth = getEstimateWidth(
    environments.map(e => getEnvDisplayName(e, appConfigs)),
    8
  );

  const handleVersionClick = (versionCode: string) => {
    let version = appVersions.find((v: AppVersion) => v.code === versionCode);
    if (version?.id != null) {
      dispatch(selectAppVersion(version));
    }
  };

  const handleEnvClick = (envCode: string) => {
    let targetEnv = environments.find(
      (env: Environment) => env.code === envCode
    );
    if (targetEnv?.id != null) {
      dispatch(selectEnv(targetEnv));
    }
  };

  const downloadConfigFile = () => {
    runAsync(async () => {
      if (selectedApp && selectedAppVersion) {
        dispatch(showLoading());
        const downloadPandoraConfigResult = await downloadPandoraConfig(
          selectedApp,
          selectedAppVersion
        );
        dispatch(hideLoading());
        if (downloadPandoraConfigResult.isError()) {
          showError(downloadPandoraConfigResult.exception().message);
          return;
        }
      }
    });
  };

  const showBuildAppPopup = () => {
    setShowBuildApp(true);
  };

  const showQRPopup = () => {
    setShowQR(true);
  };

  const addVersion = () => {
    setShowAddVersion(true);
  };

  const filterVersions = (value: string) => {
    const filteredVersions = appVersions.filter(version =>
      version.code.toLowerCase().includes(value.trim().toLowerCase())
    );

    setFilteredAppVersions(filteredVersions);
  };

  const onBuildAppResult = (result: Result<BuildAppVersionResponse>) => {
    setBuildAppResult({
      visible: true,
      isSuccess: result.isSuccess(),
      title: result.isSuccess()
        ? t('build_and_release_build_app_popup_result_success_title')
        : t('build_and_release_build_app_popup_result_error_title'),
      message: result.isSuccess()
        ? t('build_and_release_build_app_popup_result_success_message')
        : extractBuildAppErrorMessage(result),
    });
  };

  const onCloseResultModal = () => {
    setBuildAppResult({
      visible: false,
      isSuccess: false,
    });
    setShowBuildApp(false);
    document.getElementById('reload-build-app-history')?.click()
  };

  const handleMenuActionClick: MenuProps['onClick'] = e => {
    switch (e.key) {
      case MenuAction.DownloadConfig:
        downloadConfigFile();
        break;
      case MenuAction.ShowQrCode:
        showQRPopup();
        break;
    }
  };

  const items: MenuProps['items'] = [
    {
      label: t('Download Config'),
      key: MenuAction.DownloadConfig,
      icon: <div>{DownloadIcon}</div>,
    },
    {
      label: t('terra_app_qr_title'),
      key: MenuAction.ShowQrCode,
      icon: <div>{QrCodeIcon}</div>,
    },
  ];

  const menuActionProps = {
    items,
    onClick: handleMenuActionClick,
  };

  return (
    <div className="app-detail-selector">
      {filterDisplayPlatform(platforms).map(p => {
        return (
          <div
            key={p.code}
            className="platform-selector-container"
            style={{
              backgroundColor:
                selectedApp?.platform?.code === p.code
                  ? '#DCDEE9'
                  : 'transparent',
            }}
            onClick={() => {
              let targetApp = apps.find((a: App) => a.platform.code === p.code);
              if (targetApp?.id != null) {
                dispatch(selectApp(targetApp?.id));
              }
            }}
          >
            {mapPlatformIcon(p.code)}
          </div>
        );
      })}

      <Select
        showSearch={true}
        onSearch={filterVersions}
        filterOption={false}
        className="section"
        value={selectedAppVersion?.code}
        onChange={handleVersionClick}
        style={{ width: estimatedVersionWidth }}
      >
        {filteredAppVersions.map((v: AppVersion) => (
          <Select.Option key={v.code}>{v.code}</Select.Option>
        ))}
      </Select>

      <Select
        className="section"
        value={selectedEnv?.code}
        onChange={handleEnvClick}
        style={{ width: estimatedEnvWidth }}
      >
        {environments.map((e: Environment) => (
          <Select.Option key={e.code}>
            {getEnvDisplayName(e, appConfigs)}
          </Select.Option>
        ))}
      </Select>

      <div
        id={ElementIds.SyncConfigButton}
        className="section-icon"
        hidden={!visibleSyncConfigButton}
      >
        {SyncIcon}
      </div>

      <Tooltip
        placement="top"
        title={t('build_and_release_build_button_tooltip')}
      >
        <div className="section-icon" onClick={() => showBuildAppPopup()}>
          {InstallmentIcon}
        </div>
      </Tooltip>

      <Dropdown menu={menuActionProps}>
        <div className="section-icon">{MoreVerticalIcon}</div>
      </Dropdown>

      <div style={{ flex: 1 }} />

      <Tooltip placement="top">
        <Button
          className="primary-button"
          type="primary"
          onClick={addVersion}
          style={{ marginRight: '8px', marginLeft: '8px' }}
        >
          {t('add_version')}
        </Button>
      </Tooltip>

      <Tooltip
        placement="top"
        title={t('switch_env_tooltip', {
          env: activeEnv?.name?.toUpperCase() ?? '',
        })}
      >
        <Button
          className="secondary-button"
          type="primary"
          onClick={changeEnvironmentConfig}
          disabled={isSelectedEnvActiveEnv}
        >
          {t('switch_config_env')}
        </Button>
      </Tooltip>

      {showQR && (
        <ClientQrPopup
          handleCloseModal={() => {
            setShowQR(false);
          }}
        />
      )}

      {showAddVersion && selectedApp && (
        <AddVersionPopup
          app={selectedApp}
          versions={appVersions}
          handleCloseModal={() => {
            setShowAddVersion(false);
          }}
          handleOpenVersionNamingRule={() => {
            setShowVersionNamingRule(true);
          }}
        />
      )}

      {showVersionNamingRule && (
        <VersionNamingRulePopup
          handleCloseModal={() => {
            setShowVersionNamingRule(false);
          }}
        />
      )}

      {showBuildApp && (
        <BuildAppPopup
          handleCloseModal={() => {
            setShowBuildApp(false);
          }}
          onFinishWithResult={onBuildAppResult}
        />
      )}

      <ResultModal
        visible={buildAppResult.visible}
        isSuccess={buildAppResult.isSuccess}
        title={buildAppResult.title}
        message={buildAppResult.message}
        primaryAction={{
          text: t('close'),
          onClick: onCloseResultModal,
        }}
      />
    </div>
  );
};

const stateSelector = (rootState: RootState) => {
  const projectDetail = rootState.projectDetail;
  const { environments, platforms } = rootState.common;

  return {
    environments,
    platforms,
    apps: projectDetail.apps.data ?? [],
    appVersions: projectDetail.appVersions.data ?? [],
    selectedEnv: projectDetail.selectedEnv,
    selectedApp: projectDetail.selectedApp,
    selectedAppVersion: projectDetail.selectedAppVersion,
    appConfigs: projectDetail.appConfigs.data ?? [],
    visibleSyncConfigButton: projectDetail.uiConfig.visibleSyncConfigButton,
  };
};

export default AppDetailSelector;

const filterDisplayPlatform = (platforms: Platform[]) => {
  return platforms.filter(
    p =>
      p.code === PlatformCode.AndroidNative || p.code === PlatformCode.IOSNative
  );
};

const mapPlatformIcon = (platformCode: string) => {
  switch (platformCode) {
    case PlatformCode.AndroidNative:
      return (
        <div className="platform-item">
          <div style={{ marginRight: '8px' }}>{AndroidIcon}</div>
          Android
        </div>
      );
    case PlatformCode.IOSNative:
      return (
        <div className="platform-item">
          <div style={{ marginRight: '8px' }}>{IosIcon}</div>
          iOS
        </div>
      );
    default:
      return null;
  }
};

const extractBuildAppErrorMessage = (
  result: Result<BuildAppVersionResponse>
): string => {
  if (result.isError()) {
    const axiosError = asAxiosError(result.error);
    const errorResponse = (axiosError?.response
      ?.data as unknown) as ErrorResponse;
    if (axiosError?.response?.status === 400) {
      if (
        errorResponse.code === 400002 &&
        errorResponse.message === 'RegisteredEmails is invalid'
      ) {
        return t('build_and_release_error_invalid_email');
      } else if (
        errorResponse.code === 400002 &&
        errorResponse.message === 'BranchName is invalid'
      ) {
        return t('build_and_release_error_invalid_branch');
      } else if (errorResponse.code === 400502) {
        return t('build_and_release_error_gitlab_server');
      }
    } else if (axiosError?.response?.status === 403) {
      return t('build_and_release_error_gitlab_permission_denied');
    } else if (axiosError?.response?.status === 404) {
      if (errorResponse.code === 404007) {
        return t('build_and_release_error_gitlab_not_found_app_version');
      } else if (errorResponse.code === 404132) {
        return t('build_and_release_error_gitlab_not_found_build_flavor');
      } else if (errorResponse.code === 404143) {
        return t('build_and_release_error_gitlab_not_found_project');
      }
    } else if (axiosError?.response?.status === 500) {
      if (errorResponse.code === 500000) {
        return t('build_and_release_error_internal_server_error');
      } else if (errorResponse.code === 500140) {
        return t('build_and_release_error_gitlab_internal_server_error');
      }
    }

    return t('build_and_release_build_app_popup_result_error_message');
  } else {
    return '';
  }
};
